import React, { useCallback, useEffect, useState } from 'react'
import { Box, Typography, alpha, useTheme } from '@mui/material'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import { toast } from 'react-toastify'
import DashboardServices from 'services/DashboardServices'
import DashboardContainer from 'components/DashboardContainer'

const CATEGORY_COLORS = [
    '#8859f4',
    '#2181f5',
    '#fa9e1b',
    '#ef4444',
    '#10b981',
    '#8b5cf6',
    '#06b6d4',
    '#f97316',
    '#84cc16',
    '#ec4899',
    '#64748b',
]

const CommentsByCategory = ({ filters = {} }) => {
    const theme = useTheme()
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const [total, setTotal] = useState(0)

    const getData = useCallback(async () => {
        setLoading(true)
        try {
            const response = await DashboardServices.getCommentsByStatus(filters)

            if (response.success) {
                const commentsByCategory = response?.data?.data?.data?.comments_by_category || []

                // Aggregate duplicate category entries by category id
                const aggregated = {}
                commentsByCategory.forEach((item) => {
                    const key = item.comment_category_id
                    if (aggregated[key]) {
                        aggregated[key].value += Number(item.count) || 0
                    } else {
                        aggregated[key] = {
                            categoryId: key,
                            code: item.comment_category_code,
                            name: item.comment_category,
                            value: Number(item.count) || 0,
                        }
                    }
                })

                const mappedData = Object.values(aggregated)
                    .map((item, index) => ({
                        ...item,
                        color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
                    }))

                const totalCount = mappedData.reduce((sum, item) => sum + item.value, 0)

                setData(mappedData)
                setTotal(totalCount)
            } else {
                setData([])
                setTotal(0)
                toast.error(response.message || 'Failed to load comments data.')
            }
        } finally {
            setLoading(false)
        }
    }, [filters])

    useEffect(() => {
        getData()
    }, [getData])

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                        borderRadius: 1,
                        p: 1,
                    }}
                >
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: theme.palette.text.primary }}>
                        {payload[0].payload.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: payload[0].payload.color, fontWeight: 700 }}>
                        {payload[0].value} comment{payload[0].value !== 1 ? 's' : ''}
                    </Typography>
                </Box>
            )
        }
        return null
    }

    const CustomLegend = (props) => {
        const { payload } = props
        return (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                {payload && payload.map((entry, index) => {
                    const dataItem = data[index]
                    return (
                        <Box
                            key={`legend-${index}`}
                            sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: entry.color,
                                    }}
                                />
                                <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.primary }}>
                                    {entry.value}
                                </Typography>
                            </Box>
                            <Typography sx={{ minWidth: 20, textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: theme.palette.text.primary }}>
                                {dataItem?.value}
                            </Typography>
                        </Box>
                    )
                })}
            </Box>
        )
    }

    return (
        <DashboardContainer
            title="Comments by Category"
        >
            {loading && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography sx={{ color: theme.palette.text.secondary }}>
                        Loading comments data...
                    </Typography>
                </Box>
            )}

            {!loading && data.length === 0 && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography sx={{ color: theme.palette.text.secondary }}>
                        No comments data available for the selected period.
                    </Typography>
                </Box>
            )}

            {!loading && data.length > 0 && (
                <Box className="w-full" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                    <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                            <Pie
                                data={data.filter((d) => d.value > 0)}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={95}
                                paddingAngle={2}
                                dataKey="value"
                            >
                                {data.filter((d) => d.value > 0).map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: alpha(theme.palette.primary.main, 0.08) }} />
                        </PieChart>
                    </ResponsiveContainer>

                    <CustomLegend payload={data.map((item) => ({ value: item.name, color: item.color }))} />
                </Box>
            )}
        </DashboardContainer>
    )
}

export default CommentsByCategory
